import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Key, Globe, Shield, Mail, Users, Save } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';

interface MicrosoftSettings {
  clientId: string;
  clientSecret: string;
  tenantId: string;
  graphMailFrom: string;
  entraGroupAgents: string;
  entraGroupAdmins: string;
  entraSuperAdmins: string;
}

export function MicrosoftPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<MicrosoftSettings>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'microsoft'],
    queryFn: () => api.get<MicrosoftSettings>('/settings/microsoft'),
  });
  const settings = data?.data;

  const saveMutation = useMutation({
    mutationFn: (data: Partial<MicrosoftSettings>) => api.put('/settings/microsoft', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'microsoft'] });
      setEditing(false);
      toast('success', 'Setările Microsoft au fost salvate cu succes');
    },
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });

  const startEdit = () => {
    setForm({
      tenantId: settings?.tenantId ?? '',
      clientId: settings?.clientId ?? '',
      clientSecret: '',
      graphMailFrom: settings?.graphMailFrom ?? '',
      entraGroupAgents: settings?.entraGroupAgents ?? '',
      entraGroupAdmins: settings?.entraGroupAdmins ?? '',
      entraSuperAdmins: settings?.entraSuperAdmins ?? '',
    });
    setEditing(true);
  };

  const handleSave = () => {
    const payload: Record<string, string> = {};
    if (form.tenantId && form.tenantId !== settings?.tenantId) payload.tenantId = form.tenantId;
    if (form.clientId && form.clientId !== settings?.clientId) payload.clientId = form.clientId;
    if (form.clientSecret) payload.clientSecret = form.clientSecret;
    if (form.graphMailFrom && form.graphMailFrom !== settings?.graphMailFrom) payload.graphMailFrom = form.graphMailFrom;
    if (form.entraGroupAgents !== undefined && form.entraGroupAgents !== settings?.entraGroupAgents) payload.entraGroupAgents = form.entraGroupAgents;
    if (form.entraGroupAdmins !== undefined && form.entraGroupAdmins !== settings?.entraGroupAdmins) payload.entraGroupAdmins = form.entraGroupAdmins;
    if (form.entraSuperAdmins !== undefined && form.entraSuperAdmins !== settings?.entraSuperAdmins) payload.entraSuperAdmins = form.entraSuperAdmins;
    if (Object.keys(payload).length > 0) saveMutation.mutate(payload);
    else setEditing(false);
  };

  const Field = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-start gap-3">
      <Key size={18} className="mt-0.5 text-gray-400 shrink-0" />
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
        <p className="text-sm font-mono text-gray-900 dark:text-dark-text">{value || '— nesetat —'}</p>
      </div>
    </div>
  );

  const EditField = ({ label, field, type = 'text', placeholder }: { label: string; field: keyof MicrosoftSettings; type?: string; placeholder?: string }) => (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase">{label}</label>
      <input
        type={type}
        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
        value={(form as Record<string, string>)[field] ?? ''}
        onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Microsoft Entra ID & Graph</h1>
        {!editing && (
          <Button variant="outline" onClick={startEdit}>Editează</Button>
        )}
      </div>

      {editing ? (
        <Card>
          <CardHeader>
            <h2 className="font-semibold flex items-center gap-2"><Shield size={18} /> Editare credențiale</h2>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <EditField label="Tenant ID" field="tenantId" />
              <EditField label="Client ID" field="clientId" />
              <EditField label="Client Secret (lasă gol pentru a păstra cel existent)" field="clientSecret" type="password" placeholder="••••••••" />
              <EditField label="Email expeditor (Graph)" field="graphMailFrom" />
              <EditField label="Grup Agenți" field="entraGroupAgents" />
              <EditField label="Grup Admini" field="entraGroupAdmins" />
              <EditField label="Super Admini (email-uri separate cu virgulă)" field="entraSuperAdmins" />
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saveMutation.isPending}>
                  <Save size={16} /> Salvează
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}>Anulează</Button>
              </div>
              {saveMutation.isError && (
                <p className="text-sm text-red-600">Eroare la salvare: {(saveMutation.error as Error).message}</p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Entra ID Credentials */}
          <Card>
            <CardHeader>
              <h2 className="font-semibold flex items-center gap-2"><Shield size={18} /> Credențiale Entra ID</h2>
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-32 w-full" /> : (
                <div className="space-y-3">
                  <Field label="Tenant ID" value={settings?.tenantId ?? ''} />
                  <Field label="Client ID" value={settings?.clientId ?? ''} />
                  <Field label="Client Secret" value={settings?.clientSecret ?? ''} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Graph Mail */}
          <Card>
            <CardHeader>
              <h2 className="font-semibold flex items-center gap-2"><Mail size={18} /> Microsoft Graph – Email</h2>
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-12 w-full" /> : (
                <Field label="Email expeditor (GRAPH_MAIL_FROM)" value={settings?.graphMailFrom ?? ''} />
              )}
            </CardContent>
          </Card>

          {/* Entra Groups */}
          <Card>
            <CardHeader>
              <h2 className="font-semibold flex items-center gap-2"><Users size={18} /> Grupuri Entra ID</h2>
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-24 w-full" /> : (
                <div className="space-y-3">
                  <Field label="Grup Agenți (ENTRA_GROUP_AGENTS)" value={settings?.entraGroupAgents ?? ''} />
                  <Field label="Grup Admini (ENTRA_GROUP_ADMINS)" value={settings?.entraGroupAdmins ?? ''} />
                  <Field label="Super Admini (ENTRA_SUPER_ADMINS)" value={settings?.entraSuperAdmins ?? ''} />
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader><h2 className="font-semibold">Informații importante</h2></CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <p>• Autentificarea staff-ului se face prin Microsoft Entra ID (OAuth2).</p>
          <p>• Email-urile de notificare sunt trimise prin Microsoft Graph API.</p>
          <p>• Rolurile sunt asociate pe baza grupurilor Entra ID configurate mai sus.</p>
          <p>• Setările sunt stocate în baza de date și suprascriu valorile din <code>.env</code>.</p>
        </CardContent>
      </Card>
    </div>
  );
}
